/**
 * Game arena — wraps the active game with team info, scoreboard, and game board.
 */

import { useMemo } from "react";
import { TicTacToeBoard } from "./TicTacToeBoard";
import type { GameState } from "@/hooks/useGame";
import { Shield, X, Circle } from "lucide-react";

interface GameArenaProps {
  gameState: GameState;
  winningLine: number[] | null;
  onMakeMove: (cell: number) => void;
}

export function GameArena({ gameState, winningLine, onMakeMove }: GameArenaProps) {
  const { gameInfo, ticTacToe, myTeam } = gameState;

  const opponentTeam = myTeam === "a" ? ("b" as const) : ("a" as const);

  const isGameOver = ticTacToe.winner !== null;
  const isDraw = ticTacToe.winner === "draw";
  const iWon = ticTacToe.winner === myTeam;
  const opponentWon = ticTacToe.winner === opponentTeam;

  const gameTypeNames: Record<string, string> = {
    tictactoe: "Tic-Tac-Toe",
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {/* Game header */}
      <div className="mb-6 text-center">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {gameTypeNames[gameInfo.gameType] ?? gameInfo.gameType}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Game ID: <code className="rounded bg-muted px-1 py-0.5 text-[10px]">{gameInfo.gameId.slice(0, 12)}...</code>
        </p>
      </div>

      {/* Team scoreboard */}
      <div className="mb-8 grid grid-cols-3 items-center gap-4">
        {/* Team A */}
        <div className={`rounded-xl border p-3 text-center ${
          myTeam === "a" ? "border-primary/50 bg-primary/5" : "border-border bg-card"
        }`}>
          <Shield className={`mx-auto mb-1 h-5 w-5 ${myTeam === "a" ? "text-primary" : "text-muted-foreground"}`} />
          <p className="text-xs font-semibold text-foreground truncate">
            {gameInfo.teamAName || "Team A"}
          </p>
          <p className="text-[10px] text-muted-foreground">
            <X className="inline h-3 w-3 text-blue-500" /> X
          </p>
          {myTeam === "a" && (
            <span className="mt-1 inline-block rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-medium text-primary">
              You
            </span>
          )}
        </div>

        {/* VS */}
        <div className="text-center">
          {isGameOver ? (
            <div className={`rounded-xl px-3 py-2 text-sm font-bold ${
              isDraw
                ? "bg-yellow-500/10 text-yellow-500"
                : iWon
                  ? "bg-green-500/10 text-green-500"
                  : "bg-destructive/10 text-destructive"
            }`}>
              {isDraw ? "Draw!" : iWon ? "Victory!" : "Defeat"}
            </div>
          ) : (
            <div className="text-lg font-black text-muted-foreground/40">VS</div>
          )}
          <p className="mt-1 text-[10px] text-muted-foreground">
            Move {ticTacToe.moveCount + 1}/9
          </p>
        </div>

        {/* Team B */}
        <div className={`rounded-xl border p-3 text-center ${
          myTeam === "b" ? "border-primary/50 bg-primary/5" : "border-border bg-card"
        }`}>
          <Shield className={`mx-auto mb-1 h-5 w-5 ${myTeam === "b" ? "text-primary" : "text-muted-foreground"}`} />
          <p className="text-xs font-semibold text-foreground truncate">
            {gameInfo.teamBName || "Team B"}
          </p>
          <p className="text-[10px] text-muted-foreground">
            <Circle className="inline h-3 w-3 text-red-500" /> O
          </p>
          {myTeam === "b" && (
            <span className="mt-1 inline-block rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-medium text-primary">
              You
            </span>
          )}
        </div>
      </div>

      {/* Game board */}
      <TicTacToeBoard
        board={ticTacToe.board}
        winningLine={winningLine}
        isMyTurn={gameState.isMyTurn}
        myTeam={myTeam}
        onCellClick={onMakeMove}
        disabled={isGameOver}
      />

      {/* Game over actions */}
      {isGameOver && (
        <div className="mt-6 text-center">
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Play Again
          </button>
        </div>
      )}
    </div>
  );
}